import { CompactPage } from '~/models/common/types'
import {
  SearchTitlePart,
  Category,
  Facet,
  MassActions,
  Pagination,
  SearchLink,
  SearchSellerRow,
  SellerClassifieds,
  SellersInfo,
  SortField,
  Stats,
  TextSearch,
  ClassifiedSearchResultRow,
  ClassifiedSearchResultsMoreResultsButton
} from '~/models/search/types'
import { ViewType } from '~/models/shared/types'
import { DEFAULT_NUMBER_OF_RESULTS_PER_PAGE } from '~/constants/search/defaults'
import { AdsTargeting, BackendAdsConfig } from '~/models/ads'
import { PaidClassifiedsInfoSummary } from '~/models/classified/paid/types'
import { SearchConfig } from '~/models/search/config'

export const CLASSIFIED_SEARCH_NS = 'classifieds/search'

export interface SearchRoute {
  path: string
  query: Record<string, any>
}

export interface SearchState {
  config: SearchConfig
  params: Record<string, any> & { pg?: number }
  newWantedClassifiedUrl?: string
  showInPlotUrl?: string
  perPage: number
  args: (Record<string, any> & { category: string[] }) | {}
  category: Category | undefined
  categoryIds: number[] // Contains ancestor and own ids
  facets: Facet[]
  rows: ClassifiedSearchResultRow[] | null
  compare: {
    ids: number[]
  }
  clusterRows: ClassifiedSearchResultRow[] | null
  sellerRows: SearchSellerRow[] | null
  pagination: Pagination
  clusterPagination: Pagination
  shareable: boolean
  savable: boolean
  loading: {
    results: boolean
    facets: boolean
    map: boolean
    clusterResults: boolean
  }
  isFavorite?: boolean
  notifications: {
    instantValid?: boolean
    bulkValid?: boolean
  }
  previousSearch: { name: string; search: string; time: number } | null
  viewType: ViewType
  adsTargetings?: AdsTargeting[]
  canBeSaved?: boolean
  changeSearchUrl?: string
  longTitle?: SearchTitlePart[]
  massActions: MassActions
  mySearchLinks?: SearchLink[]
  userProfile?: object | null
  newClassifiedUrl?: string
  mapSearchUrl?: string
  searchId?: string | null
  shortTitle?: SearchTitlePart[]
  sortFields?: SortField[]
  stats: Stats
  title?: string
  textSearch?: TextSearch
  page?: CompactPage
  showMobileFacets: boolean
  sellersClassifiedsInfo?: SellerClassifieds
  sellersInfo?: SellersInfo
  paidSummary?: PaidClassifiedsInfoSummary
  // TODO: type these
  map: {
    clusters: any
    markers: any
  }
  flags: {
    isSellersOnlySearch: boolean
    isMapSearch: boolean
  }
  // We are forced to keep the route state here since we can not consistently use the $route property in search
  // we can probably remove these when we migrate to vue-router 4 (vue 3)
  currentRoute: SearchRoute
  previousRoute: SearchRoute
  userOwnsSearch: boolean
  backendAdsConfig: BackendAdsConfig
  notMyClassifiedsUrl?: string
  scrollTop: number
  moreResultsButton: ClassifiedSearchResultsMoreResultsButton | null
}

function getDefaultState(): SearchState {
  return {
    category: undefined,
    categoryIds: [],
    config: {
      settings: {}
    },
    params: {},
    perPage: DEFAULT_NUMBER_OF_RESULTS_PER_PAGE,
    args: {},
    facets: [],
    rows: null,
    compare: {
      ids: []
    },
    clusterRows: null,
    sellerRows: null,
    savable: false,
    shareable: false,
    searchId: null,
    massActions: {},
    shortTitle: [],
    sortFields: [],
    previousSearch: null,
    loading: {
      facets: true,
      results: true,
      map: true,
      clusterResults: false
    },
    viewType: ViewType.LIST,
    canBeSaved: false,
    pagination: {
      page: 1,
      pageParam: '',
      perPage: DEFAULT_NUMBER_OF_RESULTS_PER_PAGE,
      total: 0
    },
    textSearch: undefined,
    clusterPagination: { page: 1, pageParam: '', perPage: 24, total: 0 },
    page: {
      breadcrumbs: [],
      metadata: [],
      seoUrl: '',
      title: '',
      externalUrl: '',
      targetSeoUrl: ''
    },
    userProfile: null,
    isFavorite: undefined,
    notifications: {
      bulkValid: undefined,
      instantValid: undefined
    },
    showMobileFacets: false,
    sellersClassifiedsInfo: undefined,
    sellersInfo: undefined,
    paidSummary: undefined,
    stats: {
      averagePrice: 0,
      total: 0
    },
    mapSearchUrl: undefined,
    currentRoute: {
      query: {},
      path: ''
    },
    previousRoute: {
      query: {},
      path: ''
    },
    flags: {
      isSellersOnlySearch: false,
      isMapSearch: false
    },
    map: {
      clusters: [],
      markers: []
    },
    userOwnsSearch: false,
    backendAdsConfig: {
      disabled: false
    },
    scrollTop: 0,
    moreResultsButton: null
  }
}

export default getDefaultState
